import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { appConfigStore } from 'Models/AppConfigStore';
import { store } from 'Models/Store';
import { configuredHexColour } from 'Util/_HumanWritten/ColourUtils';
import { useNavigate } from 'react-router-dom';
import {
	clearBookingWizardData,
	getBookingWizardData,
} from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardDataStore';
import { TabSelected } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardTypes';
import { VehicleBookingButton } from './VehicleBookingButton';
import { PassengerBookingButton } from './PassengerBookingButton';
import { FormattedMessage } from 'react-intl';
import If from '../../If/If';
import * as React from 'react';
import { HomeProps } from './Home';
import { clearFerryBookingTransactionIdFromStorage } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import Icon from '../Icon/Icon';
import { FreightBookingButton } from './FreightBookingButton';
import { useState, useEffect } from 'react';
import classNames from 'classnames';
import PurchaseGiftVoucherModal from '../PurchaseGiftCertificate/PurchaseGiftVoucherModal';
import {
	getPendingVoucherPurchase,
	clearPendingVoucherPurchase,
} from '../PurchaseGiftCertificate/VoucherPurchasePendingFlag';
import { observer } from 'mobx-react';
import AnytimeTicketButton from './AnytimeTicketButton';

import ParkingButton from './ParkingButton';

function DefaultHome({ contactUs }: HomeProps) {
	const navigate = useNavigate();

	const [showGiftVoucherModal, setShowGiftVoucherModal] = useState(false);

	useEffect(() => {
		if (getPendingVoucherPurchase()) {
			clearPendingVoucherPurchase();
			setShowGiftVoucherModal(true);
		}
	}, []);

	const onScheduleClick = () => {
		clearBookingWizardData();
		navigate('/ferry-schedule');
	};

	const onEventsCatalogueClick = () => {
		navigate('/upcoming-events');
	};

	const onSearch = (type: TabSelected) => {
		const wizardData = getBookingWizardData({
			type: type,
		});
		if (wizardData.wizardMode === 'ALTERATION') {
			clearFerryBookingTransactionIdFromStorage();
			// This will clear the new and old booking wizard data (if any)
			clearBookingWizardData();
		}
	};

	// Show intercom if the user has entered the application from a contact us link,
	// an assumption is that they have just navigated to the page.
	if (contactUs && !!(window as any).Intercom) {
		(window as any).Intercom('show');
	}

	// Get the booking buttons in order of priority and depending on the config (whether they are enabled or not)
	const bookingButtons = [];
	// Staff without BookOnBehalfOfCustomer get none: the server refuses them every booking, their own included.
	const mayBook = store.mayMakeBookings;

	if (whiteLabelStore.defaultBookingType === 'vehicle') {
		if (appConfigStore.vehicleEnabled) {
			bookingButtons.push(<VehicleBookingButton key="vehicle-booking-button" onSearch={onSearch} />);
		}
		if (appConfigStore.showPassengerBookingOption) {
			bookingButtons.push(<PassengerBookingButton key="passenger-booking-button" onSearch={onSearch} />);
		}
		if (appConfigStore.freightEnabled) {
			bookingButtons.push(<FreightBookingButton key="freight-booking-button" onSearch={onSearch} />);
		}
	} else if (whiteLabelStore.defaultBookingType === 'passenger') {
		if (appConfigStore.showPassengerBookingOption) {
			bookingButtons.push(<PassengerBookingButton key="passenger-booking-button" onSearch={onSearch} />);
		}
		if (appConfigStore.vehicleEnabled) {
			bookingButtons.push(<VehicleBookingButton key="vehicle-booking-button" onSearch={onSearch} />);
		}
		if (appConfigStore.freightEnabled) {
			bookingButtons.push(<FreightBookingButton key="freight-booking-button" onSearch={onSearch} />);
		}
	} else if (whiteLabelStore.defaultBookingType === 'freight') {
		if (appConfigStore.freightEnabled) {
			bookingButtons.push(<FreightBookingButton key="freight-booking-button" onSearch={onSearch} />);
		}
		if (appConfigStore.vehicleEnabled) {
			bookingButtons.push(<VehicleBookingButton key="vehicle-booking-button" onSearch={onSearch} />);
		}
		if (appConfigStore.showPassengerBookingOption) {
			bookingButtons.push(<PassengerBookingButton key="passenger-booking-button" onSearch={onSearch} />);
		}
	}

	const {
		anytimeTicketingHomepageColour,
		eventsEnabled,
		homepageFaqEnabled,
		homepageFaqLink,
		voucherHomepageColour,
		voucherPurchaseEnabled,
	} = appConfigStore;

	const isLightColour = (hex: string) => {
		const c = hex.replace('#', '');
		const full =
			c.length === 3
				? c
						.split('')
						.map(ch => ch + ch)
						.join('')
				: c;
		const r = parseInt(full.substring(0, 2), 16);
		const g = parseInt(full.substring(2, 4), 16);
		const b = parseInt(full.substring(4, 6), 16);
		return (r * 299 + g * 587 + b * 114) / 1000 > 150;
	};
	const voucherCardColour = configuredHexColour(voucherHomepageColour);
	const voucherUseDarkText = voucherCardColour ? isLightColour(voucherCardColour) : false;
	const anytimeColour = configuredHexColour(anytimeTicketingHomepageColour);

	return (
		<>
			<div className="home-page__hero">
				<h1 className="home-page__title">
					<FormattedMessage
						id="homepage__hero__title"
						defaultMessage="Your Trip Starts <color>Here</color>"
						values={{
							color: (chunks: React.ReactNode) => (
								<span className="home-page__title--color">{chunks}</span>
							),
						}}
					/>
				</h1>
				<p className="home-page__subtitle hide-on-mobile">
					<FormattedMessage
						id="homepage__hero__subtitle"
						defaultMessage="Book a trip or check out our upcoming departures."
					/>
				</p>
			</div>
			<div className={classNames('home-bento', { 'home-bento--no-events': !eventsEnabled })}>
				<div className="home-bento__booking home-content__container begin-booking__container glass">
					<If condition={!mayBook}>
						{/* Staff copy, so not in the language pack. Without it the panel was left empty. */}
						<span className="begin-booking__eyebrow">Bookings</span>
						<p className="begin-booking__no-permission">
							You don&apos;t have permission to make bookings. Ask a manager for &apos;Book on behalf of
							customer&apos;.
						</p>
					</If>
					<If condition={mayBook}>
						<span className="begin-booking__eyebrow">
							<FormattedMessage id="homepage__begin-booking__label" defaultMessage="BEGIN BOOKING" />
						</span>

						<div
							className={classNames('booking-type-buttons__container', {
								'fill-row': bookingButtons.length === 1,
							})}
						>
							{bookingButtons}
						</div>
					</If>
					<AnytimeTicketButton anytimeColour={anytimeColour} />
					<ParkingButton />
					<If condition={homepageFaqEnabled && !!homepageFaqLink}>
						<div className="begin-booking__divider" />
						<div
							className="begin-booking__faq"
							data-test-id="home-faq-link"
							onClick={() => navigate(homepageFaqLink)}
							role="button"
							tabIndex={0}
							onKeyDown={e => {
								if (e.key === 'Enter') navigate(homepageFaqLink);
							}}
						>
							<span className="begin-booking__faq-text">
								<FormattedMessage id="homepage__faq__label" defaultMessage="Need help? Visit our FAQ" />
							</span>
							<Icon name="arrow_forward" classname="begin-booking__faq-arrow" useMaterialIcons />
						</div>
					</If>
				</div>
				<div
					className="home-bento__schedule home-quick-link glass"
					data-test-id="home-ferry-schedule-button"
					onClick={onScheduleClick}
					role="button"
					tabIndex={0}
					onKeyDown={e => {
						if (e.key === 'Enter') onScheduleClick();
					}}
				>
					<Icon name="directions_boat" classname="home-quick-link__icon" useMaterialIcons />
					<div className="home-quick-link__text">
						<span className="home-quick-link__label">
							<FormattedMessage id="homepage__check-schedule__button" defaultMessage="Ferry schedule" />
						</span>
						<span className="home-quick-link__subtitle">
							<FormattedMessage
								id="homepage__check-schedule__subtitle"
								defaultMessage="View upcoming departures"
							/>
						</span>
					</div>
				</div>
				<If condition={eventsEnabled}>
					<div
						className="home-bento__events home-quick-link glass"
						onClick={onEventsCatalogueClick}
						role="button"
						tabIndex={0}
						onKeyDown={e => {
							if (e.key === 'Enter') onEventsCatalogueClick();
						}}
					>
						<Icon name="celebration" classname="home-quick-link__icon" useMaterialIcons />
						<div className="home-quick-link__text">
							<span className="home-quick-link__label">
								<FormattedMessage id="homepage__events-catalogue__button" defaultMessage="Events" />
							</span>
							<span className="home-quick-link__subtitle">
								<FormattedMessage
									id="homepage__events-catalogue__subtitle"
									defaultMessage="Browse upcoming events"
								/>
							</span>
						</div>
					</div>
				</If>
				<If condition={voucherPurchaseEnabled}>
					<div
						className={classNames('home-bento__gift gift-certificate-card', {
							'gift-certificate-card--dark-text': voucherUseDarkText,
						})}
						data-test-id="home-gift-voucher-card"
						style={
							voucherCardColour
								? ({ '--gift-card-colour': voucherCardColour } as React.CSSProperties)
								: undefined
						}
						onClick={() => setShowGiftVoucherModal(true)}
						role="button"
						tabIndex={0}
						onKeyDown={e => {
							if (e.key === 'Enter') setShowGiftVoucherModal(true);
						}}
					>
						<div className="gift-certificate-card__overlay">
							<div className="gift-certificate-card__left">
								<div className="gift-certificate-card__icon-circle">
									<Icon name="redeem" classname="gift-certificate-card__icon" useMaterialIcons />
								</div>
								<div className="gift-certificate-card__text">
									<h3>
										<FormattedMessage
											id="homepage__voucher-purchase-card__title"
											defaultMessage="Gift a ferry trip"
										/>
									</h3>
									<p>
										<FormattedMessage
											id="homepage__voucher-purchase-card__subtitle"
											defaultMessage="Redeemable on any route, any time"
										/>
									</p>
								</div>
							</div>
							<span className="gift-certificate-card__buy-btn">
								<FormattedMessage
									id="homepage__voucher-purchase-card__buy-button"
									defaultMessage="BUY NOW"
								/>
							</span>
						</div>
					</div>
				</If>
			</div>
			<PurchaseGiftVoucherModal isOpen={showGiftVoucherModal} onClose={() => setShowGiftVoucherModal(false)} />
		</>
	);
}

export default observer(DefaultHome);
